// n8n Service Layer for Voice-to-Task App
// Handles all communication with n8n webhooks and workflows

import {
  getWebhookUrl,
  voiceConfig,
  ApiResponse,
  VoiceUploadResponse,
  Task,
  SSEEvent,
  MOCK_MODE,
  mockTasks
} from '../config/n8n.config';

// Get auth token from Supabase (will be set by the component using this service)
let authToken: string | null = null;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let currentUser: any = null; // Supabase user object

export const setAuthToken = (token: string | null) => {
  authToken = token;
};

export const setCurrentUser = (user: any) => {
  currentUser = user;
};

const getAuthHeaders = async () => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
    console.log('🔑 Auth token present:', authToken.substring(0, 20) + '...');
  } else {
    console.log('❌ No auth token available');
  }

  return headers;
};


const getAuthHeadersForFormData = async () => {
  const headers: HeadersInit = {};

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return headers;
};

class N8nService {
  private sseConnection: EventSource | null = null;
  private sseListeners: Map<string, (event: SSEEvent) => void> = new Map();
  private mockTasksData: Task[] = [...mockTasks];

  constructor() {
    // Force close any existing SSE connections on initialization
    this.forceCloseAllConnections();
  }

  private forceCloseAllConnections(): void {
    // Close any existing SSE connection
    if (this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
    }

    // Clear all listeners
    this.sseListeners.clear();

    // Force close any global EventSource connections that might exist
    if (typeof window !== 'undefined') {
      // Check for any existing EventSource connections and close them
      console.log('Forcing closure of any existing SSE connections');
    }
  }

  // Voice Upload and Transcription
  async uploadVoiceNote(audioBlob: Blob, metadata?: any): Promise<VoiceUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice-note.webm');
      
      if (metadata) {
        formData.append('metadata', JSON.stringify(metadata));
      }

      const response = await fetch(getWebhookUrl('voiceUpload'), {
        method: 'POST',
        body: formData,
        headers: await getAuthHeadersForFormData(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<VoiceUploadResponse> = await response.json();

      console.log('🔍 Backend response:', result);
      console.log('🔍 Backend data:', result.data);

      if (!result.success) {
        throw new Error(result.error || 'Voice upload failed');
      }

      return result.data!;
    } catch (error) {
      console.error('Error uploading voice note:', error);
      throw error;
    }
  }

  // Task Management
  async createTask(task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<Task> {
    try {
      const response = await fetch(getWebhookUrl('taskCreate'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(task),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<Task> = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Task creation failed');
      }

      return result.data!;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    if (MOCK_MODE) {
      // Update mock data
      const taskIndex = this.mockTasksData.findIndex(task => task.id === taskId);
      if (taskIndex === -1) {
        throw new Error('Task not found');
      }

      this.mockTasksData[taskIndex] = {
        ...this.mockTasksData[taskIndex],
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await new Promise(resolve => setTimeout(resolve, 200));
      return this.mockTasksData[taskIndex];
    }

    try {
      const response = await fetch(`${getWebhookUrl('taskUpdate')}/${taskId}`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<Task> = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Task update failed');
      }

      return result.data!;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }

  async deleteTask(taskId: string): Promise<void> {
    if (MOCK_MODE) {
      // Remove from mock data
      const taskIndex = this.mockTasksData.findIndex(task => task.id === taskId);
      if (taskIndex === -1) {
        throw new Error('Task not found');
      }

      this.mockTasksData.splice(taskIndex, 1);
      await new Promise(resolve => setTimeout(resolve, 200));
      return;
    }

    try {
      const response = await fetch(`${getWebhookUrl('taskDelete')}/${taskId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Task deletion failed');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  async moveTask(taskId: string, newQuadrant: string): Promise<Task> {
    if (MOCK_MODE) {
      // Update mock data
      const taskIndex = this.mockTasksData.findIndex(task => task.id === taskId);
      if (taskIndex === -1) {
        throw new Error('Task not found');
      }

      this.mockTasksData[taskIndex] = {
        ...this.mockTasksData[taskIndex],
        quadrant: newQuadrant as any,
        updatedAt: new Date().toISOString(),
      };

      await new Promise(resolve => setTimeout(resolve, 200));
      return this.mockTasksData[taskIndex];
    }

    try {
      const response = await fetch(`${getWebhookUrl('taskMove')}/${taskId}/move`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ quadrant: newQuadrant }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<Task> = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Task move failed');
      }

      return result.data!;
    } catch (error) {
      console.error('Error moving task:', error);
      throw error;
    }
  }

  async getTasks(archived = false): Promise<Task[]> {
    if (MOCK_MODE) {
      // Return mock data with a small delay to simulate network
      await new Promise(resolve => setTimeout(resolve, 500));
      return this.mockTasksData.filter(task => task.archived === archived);
    }

    try {
      const url = new URL(getWebhookUrl('taskList'));
      url.searchParams.append('archived', archived.toString());
      
      console.log('🔍 Making getTasks request to:', url.toString());
      console.log('🔍 Auth token available:', authToken ? 'Yes' : 'No');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      console.log('🔍 getTasks response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 getTasks error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result: any = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch tasks');
      }

      // Handle both direct array response and paginated response
      if (Array.isArray(result.data)) {
        return result.data;
      } else if (result.data && Array.isArray(result.data)) {
        return result.data;
      } else if (result.pagination && Array.isArray(result.data)) {
        // Paginated response from backend
        return result.data;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  }

  // Archive task methods
  async archiveTask(taskId: string): Promise<Task> {
    if (MOCK_MODE) {
      const taskIndex = this.mockTasksData.findIndex(task => task.id === taskId);
      if (taskIndex === -1) {
        throw new Error('Task not found');
      }

      this.mockTasksData[taskIndex] = {
        ...this.mockTasksData[taskIndex],
        archived: true,
        updatedAt: new Date().toISOString(),
      };

      await new Promise(resolve => setTimeout(resolve, 200));
      return this.mockTasksData[taskIndex];
    }

    try {
      const response = await fetch(`${getWebhookUrl('taskUpdate')}/${taskId}/archive`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<Task> = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Task archiving failed');
      }

      return result.data!;
    } catch (error) {
      console.error('Error archiving task:', error);
      throw error;
    }
  }

  async unarchiveTask(taskId: string): Promise<Task> {
    if (MOCK_MODE) {
      const taskIndex = this.mockTasksData.findIndex(task => task.id === taskId);
      if (taskIndex === -1) {
        throw new Error('Task not found');
      }

      this.mockTasksData[taskIndex] = {
        ...this.mockTasksData[taskIndex],
        archived: false,
        updatedAt: new Date().toISOString(),
      };

      await new Promise(resolve => setTimeout(resolve, 200));
      return this.mockTasksData[taskIndex];
    }

    try {
      const response = await fetch(`${getWebhookUrl('taskUpdate')}/${taskId}/unarchive`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<Task> = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Task unarchiving failed');
      }

      return result.data!;
    } catch (error) {
      console.error('Error unarchiving task:', error);
      throw error;
    }
  }

  async getArchivedTasks(): Promise<Task[]> {
    return this.getTasks(true);
  }

  // Real-time Updates with Server-Sent Events
  connectToSSE(): void {
    // Temporarily disabled SSE connection for Express backend
    // TODO: Implement WebSocket connection for real-time updates
    console.log('SSE connection disabled - using REST API polling instead');
    return;
  }

  disconnectSSE(): void {
    // Force close all connections
    this.forceCloseAllConnections();
  }

  // Event Listeners for SSE (disabled)
  onSSEEvent(_eventType: string, _callback: (event: SSEEvent) => void): void {
    // SSE disabled - method kept for compatibility
    console.log('SSE event listener registration disabled');
  }

  removeSSEListener(_eventType: string): void {
    // SSE disabled - method kept for compatibility
    console.log('SSE event listener removal disabled');
  }

  private handleSSEEvent(_event: SSEEvent): void {
    // SSE disabled - method kept for compatibility
    console.log('SSE event handling disabled');
  }

  // Get transcription status
  async getTranscriptionStatus(transcriptionId: string): Promise<ApiResponse<any>> {
    if (MOCK_MODE) {
      // Mock response for development
      return {
        success: true,
        data: {
          status: 'completed',
          transcription: 'Mock transcription result',
          extractedTasks: []
        },
        message: 'Mock transcription completed'
      };
    }

    try {
      const response = await fetch(`${getWebhookUrl('voiceUpload').replace('/upload', '')}/transcription/${transcriptionId}`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<any> = await response.json();
      return result;
    } catch (error) {
      console.error('Error getting transcription status:', error);
      throw error;
    }
  }

  // Utility Methods
  async checkN8nHealth(): Promise<boolean> {
    if (MOCK_MODE) {
      // In mock mode, always return true
      return true;
    }

    try {
      const response = await fetch(`${getWebhookUrl('taskList')}`, {
        method: 'HEAD',
        headers: await getAuthHeaders(),
      });
      return response.ok;
    } catch (error) {
      console.error('n8n health check failed:', error);
      return false;
    }
  }

  // Audio Processing Utilities
  static async convertAudioToWebM(audioBlob: Blob): Promise<Blob> {
    // If already WebM, return as-is
    if (audioBlob.type.includes('webm')) {
      return audioBlob;
    }

    // For now, return the original blob
    // In a production app, you might want to use a library like FFmpeg.js
    // to convert audio formats
    return audioBlob;
  }

  static validateAudioFile(file: Blob): boolean {
    const maxSize = voiceConfig.upload.maxFileSize;
    
    if (file.size > maxSize) {
      throw new Error(`File size (${file.size} bytes) exceeds maximum allowed size (${maxSize} bytes)`);
    }

    const allowedTypes = ['audio/webm', 'audio/wav', 'audio/mp3', 'audio/ogg'];
    if (!allowedTypes.some(type => file.type.includes(type.split('/')[1]))) {
      throw new Error(`Unsupported audio format: ${file.type}`);
    }

    return true;
  }
}

// Export singleton instance
export const n8nService = new N8nService();
export { N8nService };
export default n8nService;
